import {
  signupSubmit,
  loginSignupPageClickOnSwitchForm,
} from '@wix/bi-logger-hls2/v2';
import { action, computed, makeObservable, observable } from 'mobx';
import { ROUTES } from '../routes';
import {
  AUTH_METHODS_BY_MAIL,
  CaptchaStatus,
  EXPERIMENTS,
  EXPERIMENT_SCOPES,
  GET_USER_ACCOUNTS_CONTEXT,
  LOGIN_PAGE_CONTEXT,
  SIGNUP_PAGE_CONTEXT,
} from '../utils/constants';
import { ERROR_CODES, extractErrorKeyByErrorCode } from '../utils/errorHandler';
import {
  Constraint,
  ERROR_TRANSLATION_KEYS,
  constraints,
  generateEmailFieldConstraints,
} from '../utils/validators';
import { CAPTCHA_ACTIONS } from './captcha';
import { FormField } from './formField';
import { LOGIN_FLOWS } from './login';
import { RootStore } from './root';
import { Values } from '../types';
import { PasswordFormField } from './passwordFormField';
import { PRESETS } from '../utils/presets';
import { postSignupEmailConfirmationFunnelExposurePointSrc5Evid1957 } from '@wix/bi-logger-identity-data/v2';
import { conductExperiment } from '../utils/conductExperiment';
import {
  shouldRemoveSignupConfirmFields,
  isEavsEmailScoreSignalEnabled,
} from './constants';
import { checkEavsSignal, peekEavsVerdict } from '../utils/eavsSignal';
import { createBiLogger } from '../services/biLogger';

export const SIGNUP_FLOWS = {
  FROM_LOGIN: 'FROM_LOGIN',
  DEFAULT: 'DEFAULT',
} as const;
export type ISignupFlows = Values<typeof SIGNUP_FLOWS>;

const MAP_FLOWS_TO_KEYS = {
  [SIGNUP_FLOWS.FROM_LOGIN]: 'signup.description.email_not_exists',
  [SIGNUP_FLOWS.DEFAULT]: undefined,
};

const EAVS_SUBMIT_WAIT_MS = 2500;

export class SignupStore implements AuthStore {
  private readonly rootStore: RootStore;
  private captchaAdded: CaptchaStatus = {
    [LOGIN_PAGE_CONTEXT]: false,
    [SIGNUP_PAGE_CONTEXT]: false,
    [GET_USER_ACCOUNTS_CONTEXT]: false,
  };
  public emailField: FormField;
  public emailRepeatField: FormField;
  public passwordField: PasswordFormField;
  public passwordRepeatField: PasswordFormField;
  public isEmailSignupMode = false;
  public readonly presetSubtitleKey?: string;
  // A/B: variant "B" drops the confirm-email / confirm-password fields, so the
  // match validations are skipped for that group. Static once experiments load.
  public readonly shouldRemoveConfirmFields: boolean;
  public requireEmailConfirmation: boolean = false;
  public eavsGatedOriginalEmail = '';
  public eavsGatedReason = '';
  public eavsGatedSubmitFields: Record<string, string> | null = null;
  public eavsImpressionReported = false;
  private SIGNUP_INTERACTION_NAME = 'email-signup';
  public isLoading: boolean = false;
  public flow: ISignupFlows = SIGNUP_FLOWS.DEFAULT;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    const { i18n } = this.rootStore;
    const emailRules: Constraint[] = generateEmailFieldConstraints(
      i18n.t.bind(i18n),
    );

    const fieldRequiredConstraint: Constraint[] = [
      [constraints.required, i18n.t(ERROR_TRANSLATION_KEYS.REQUIRED)],
    ];

    this.emailField = new FormField(
      this.rootStore.userDataStore.email,
      emailRules,
    );
    this.emailRepeatField = new FormField('', emailRules);
    this.passwordField = new PasswordFormField('', fieldRequiredConstraint);
    this.passwordRepeatField = new PasswordFormField(
      '',
      fieldRequiredConstraint,
    );
    this.presetSubtitleKey =
      this.rootStore.displayStore.preset?.signup?.subtitle?.key;
    this.shouldRemoveConfirmFields = shouldRemoveSignupConfirmFields(
      this.rootStore.experiments,
    );

    makeObservable(this, {
      emailField: observable,
      emailRepeatField: observable,
      passwordField: observable,
      isLoading: observable,
      flow: observable,
      isEmailSignupMode: observable,
      requireEmailConfirmation: observable,
      eavsGatedOriginalEmail: observable,
      eavsGatedReason: observable,
      eavsGatedSubmitFields: observable,
      eavsImpressionFields: computed,
      consumeEavsGatedSubmitFields: action.bound,
      subtitleKey: computed,
      titleKey: computed,
      signup: action.bound,
      clear: action.bound,
      onInitFields: action.bound,
      navigateToLogin: action.bound,
      handleSubmit: action.bound,
      updateEmailFieldMatchError: action.bound,
      onBackClicked: action.bound,
    });

    if (this.rootStore.socialAuthStore.isUnsupportedAgentForSocialAuth) {
      this.isEmailSignupMode = true;
    }
  }

  clear(): void {
    this.emailField.clear();
    this.emailRepeatField.clear();
    this.passwordField.clear();
    this.passwordRepeatField.clear();
    this.requireEmailConfirmation = false;
    this.eavsGatedOriginalEmail = '';
    this.eavsGatedReason = '';
    this.eavsGatedSubmitFields = null;
    this.eavsImpressionReported = false;
    this.isLoading = false;
    this.captchaAdded = {
      [LOGIN_PAGE_CONTEXT]: false,
      [SIGNUP_PAGE_CONTEXT]: false,
      [GET_USER_ACCOUNTS_CONTEXT]: false,
    };
  }

  async onNavigateToSignup(flow: ISignupFlows = SIGNUP_FLOWS.DEFAULT) {
    this.flow = flow;
    this.onInitFields();
    if (this.isEavsSignalEnabled && this.emailField.isValid) {
      this.isLoading = true;
      try {
        await this.prefetchEavsVerdictWithinBudget();
      } finally {
        this.isLoading = false;
      }
    }
    this.reestablishEavsGate();
    if (
      this.rootStore.displayStore.isVerticalLayout ||
      this.rootStore.displayStore.isMobile
    ) {
      this.isEmailSignupMode = true;
    }
    this.rootStore.navigationStore.navigate(
      ROUTES.SIGNUP_PASSWORD,
      this.flow === SIGNUP_FLOWS.FROM_LOGIN ? 'Signup - from login' : undefined,
    );
  }

  onInitFields(): void {
    this.clear();
    const defaultEmail = this.rootStore.userDataStore.email;
    this.emailField.value = defaultEmail;
  }

  get shouldDisplayOneTap() {
    return (
      !this.rootStore.displayStore.isMobile &&
      this.rootStore.displayStore.viewMode !== 'MODAL' &&
      !this.rootStore.displayStore.presetKey &&
      this.rootStore.experiments.enabled(
        EXPERIMENTS.SHOULD_DISPLAY_GOT_ON_SIGNUP_PAGE,
      )
    );
  }

  isSiteBuildingUserType() {
    return (
      (window.__USER_TYPE__ === null || window.__USER_TYPE__ === '') &&
      window.__BASE_DOMAIN__ === 'wix.com'
    );
  }

  async shouldStartEmailConfirmation() {
    const initialConditions =
      !this.rootStore.displayStore.isMobile &&
      !this.rootStore.isStudio &&
      this.isSiteBuildingUserType();
    if (!initialConditions) {
      return false;
    }
    const experimentEnabled = await this.isEmailConfirmationExperimentEnabled();
    await this.rootStore.biLogger
      .report(postSignupEmailConfirmationFunnelExposurePointSrc5Evid1957({}))
      .catch(() => {});
    return experimentEnabled;
  }

  private async isEmailConfirmationExperimentEnabled(): Promise<boolean> {
    try {
      return await conductExperiment({
        key: EXPERIMENTS.SHOULD_DISPLAY_EMAIL_CONFIRM_POST_SIGNUP,
        scope: EXPERIMENT_SCOPES.EMAIL_CONFIRMATION_ENFORCEMENT,
      });
    } catch (error) {
      return false;
    }
  }

  private async startEmailConfirmation(
    email: string,
    signupData: any,
  ): Promise<boolean> {
    try {
      const verificationSent =
        await this.rootStore.postSignupEmailConfirmStore.sendVerificationEmail(
          email,
        );
      if (verificationSent) {
        this.rootStore.postSignupEmailConfirmStore.setRegistrationPayload(
          signupData?.payload ?? {},
        );
        this.rootStore.postSignupEmailConfirmStore.resendSuccessfullyIndication =
          true;
        await this.rootStore.navigationStore.navigate(
          ROUTES.POST_SIGNUP_EMAIL_CONFIRMATION,
        );
        return true;
      }
      return false;
    } catch (error) {
      return false;
    }
  }
  get titleKey() {
    const { signup } = this.rootStore.displayStore.preset;
    if (signup?.title?.key) {
      return signup.title?.key;
    }
    return 'signup.title';
  }

  get subtitleKey() {
    const mapper = MAP_FLOWS_TO_KEYS;
    const defaultKey = mapper[SIGNUP_FLOWS.DEFAULT];
    if (
      this.flow === SIGNUP_FLOWS.FROM_LOGIN &&
      this.rootStore.userType === PRESETS.WIXEL
    ) {
      return 'signup.wixel.description.email_not_exists';
    }

    const useDefaultKey =
      this.flow === SIGNUP_FLOWS.DEFAULT || !mapper[this.flow];
    return useDefaultKey ? defaultKey : mapper[this.flow];
  }

  async signup() {
    const eavsOn = this.isEavsSignalEnabled;
    if (eavsOn && this.isLoading) {
      return;
    }
    this.rootStore.biLogger.report(signupSubmit({}));
    if (!this.isSubmittable) {
      this.markFormAsDirty();
      return;
    }
    if (this.eavsGatedOriginalEmail) {
      this.eavsGatedSubmitFields = this.buildEavsGatedSubmitFields();
      this.eavsGatedOriginalEmail = '';
      this.eavsGatedReason = '';
    }
    this.rootStore.panorama.startTransaction(this.SIGNUP_INTERACTION_NAME);
    this.isLoading = true;
    const recaptchaParams =
      await this.rootStore.captchaStore.handleRecaptchaExecution({
        captchaAdded: this.captchaAdded[SIGNUP_PAGE_CONTEXT],
        action: CAPTCHA_ACTIONS.SIGNUP,
      });
    this.rootStore.apiStore
      .signup(
        this.rootStore.apiStore.applyAdditionalSignupParams({
          email: this.emailField.value,
          password: this.passwordField.value,
          ...recaptchaParams,
        }),
      )
      .then(async (data) => {
        this.rootStore.panorama.finishTransaction(this.SIGNUP_INTERACTION_NAME);
        this.rootStore.biLogger = createBiLogger();
        this.rootStore.postLoginStore.biLogger = createBiLogger();
        const shouldStartEmailConfirmation =
          await this.shouldStartEmailConfirmation();
        if (shouldStartEmailConfirmation) {
          // extract email from signup response - try IAM format first, fallback to legacy format
          const email =
            data?.data?.identity?.email?.address ?? data?.payload?.email;
          const isEmailConfirmInitiated = await this.startEmailConfirmation(
            email,
            data,
          );
          if (isEmailConfirmInitiated) {
            return;
          }
        }
        return this.rootStore.navigationStore.postSignup(data?.payload ?? {});
      })
      .catch((error) => {
        const errorCode = error.errorCode.toString();
        if (errorCode !== ERROR_CODES.GENERAL_ERROR_CODE) {
          this.rootStore.panorama.finishTransaction(
            this.SIGNUP_INTERACTION_NAME,
          );
        }
        if (
          errorCode === ERROR_CODES.EMAIL_ALREADY_EXISTS ||
          errorCode === ERROR_CODES.EMAIL_ALREADY_EXISTS_IAM
        ) {
          return this.navigateToLogin();
        }
        if (errorCode === ERROR_CODES.STUDIO_ACCESS_DENIED) {
          return this.rootStore.navigationStore.redirect(
            'https://www.wix.com/studio/waitlist/early-access/access-denied',
          );
        }
        this.isLoading = false;
        this.captchaAdded[SIGNUP_PAGE_CONTEXT] =
          this.rootStore.captchaStore.createOrResetCaptchaIfNeeded(
            errorCode,
            this.captchaAdded[SIGNUP_PAGE_CONTEXT],
          );
        if (!this.rootStore.captchaStore.isCaptchaServerError(errorCode)) {
          this.emailField.addError(
            this.rootStore.i18n.t(
              extractErrorKeyByErrorCode(errorCode, {
                defaultError: 'errors.general.signup',
              }),
            ),
          );
          this.rootStore.modalModeHandlerStore.handleErrorReport({ errorCode });
        }
        console.error(error);
      });
  }

  private handleErrorOfEmailExists() {
    const { userDataStore, loginStore } = this.rootStore;
    userDataStore.setEmail(this.emailField.value);
    loginStore.onNavigateToLogin(LOGIN_FLOWS.FROM_SIGNUP);
    this.isLoading = false;
  }

  public async navigateToLogin() {
    const recaptchaParams =
      await this.rootStore.captchaStore.handleRecaptchaExecution({
        captchaAdded: this.captchaAdded[LOGIN_PAGE_CONTEXT],
        action: CAPTCHA_ACTIONS.LOGIN,
      });
    this.rootStore.loginStore
      .executeLogin(
        this.emailField.value,
        this.passwordField.value,
        recaptchaParams,
      )
      .catch((errorCode) => {
        if (
          this.rootStore.loginStore.shouldFetchAllAccountAfterFailedLogin(
            errorCode,
          )
        ) {
          return this.handleEmailExistsWithNoPasswordError();
        }
        if (this.rootStore.captchaStore.isCaptchaServerError(errorCode)) {
          this.isLoading = false;
          this.captchaAdded[LOGIN_PAGE_CONTEXT] =
            this.rootStore.captchaStore.createOrResetCaptchaIfNeeded(
              errorCode,
              this.captchaAdded[LOGIN_PAGE_CONTEXT],
            );
          return;
        }
        this.handleErrorOfEmailExists();
      })
      .catch(() => {
        this.handleErrorOfEmailExists();
      });
    return;
  }

  private async handleEmailExistsWithNoPasswordError() {
    this.rootStore.userDataStore.setEmail(this.emailField.value);
    const { success, errorCode } =
      await this.rootStore.loginStore.generalHandleUserWithNoPassword({
        captchaAdded: this.captchaAdded[GET_USER_ACCOUNTS_CONTEXT],
        email: this.emailField.value,
        authFlowAdditionalParmas: { showAlreadyExistsNote: true },
        originAuthMethod: AUTH_METHODS_BY_MAIL.SIGNUP_PASSWORD,
      });
    this.isLoading = false;

    if (!success) {
      this.emailField.addError(
        this.rootStore.i18n.t(extractErrorKeyByErrorCode(errorCode)),
      );
      this.captchaAdded[GET_USER_ACCOUNTS_CONTEXT] =
        this.rootStore.captchaStore.createOrResetCaptchaIfNeeded(
          errorCode,
          this.captchaAdded[GET_USER_ACCOUNTS_CONTEXT],
        );
    }
  }

  public updateEmailFieldMatchError() {
    if (!this.shouldEnforceEmailMatch) {
      return;
    }
    if (!this.doEmailsMatch() && this.emailRepeatField.isTouched) {
      !this.emailRepeatField.errors &&
        this.emailRepeatField.addError(
          this.rootStore.i18n.t(`errors.signup.emails.doNotMatch`),
        );
    } else {
      this.emailRepeatField.errors = this.emailField.errors;
    }
  }

  public updatePasswordFieldMatchError() {
    const shouldRepeatPassword =
      !this.rootStore.displayStore.isMobile && !this.shouldRemoveConfirmFields;
    if (
      shouldRepeatPassword &&
      !this.doFieldsMatch(this.passwordField, this.passwordRepeatField) &&
      this.passwordRepeatField.isTouched
    ) {
      !this.passwordRepeatField.errors &&
        this.passwordRepeatField.addError(
          this.rootStore.i18n.t(`errors.signup.passwords.doNotMatch`),
        );
    } else {
      this.passwordRepeatField.errors = this.passwordField.errors;
    }
  }

  private doFieldsMatch(field1: FormField, field2: FormField): boolean {
    return field1.value === field2.value;
  }

  private doEmailsMatch(): boolean {
    return (
      this.emailField.value.toLowerCase() ===
      this.emailRepeatField.value.toLowerCase()
    );
  }

  private get isEavsSignalEnabled(): boolean {
    return isEavsEmailScoreSignalEnabled(this.rootStore.experiments);
  }

  private get shouldEnforceEmailMatch(): boolean {
    return this.requireEmailConfirmation || !this.shouldRemoveConfirmFields;
  }

  private async prefetchEavsVerdictWithinBudget(): Promise<void> {
    let budgetTimer: ReturnType<typeof setTimeout>;
    const budget = new Promise<void>((resolve) => {
      budgetTimer = setTimeout(resolve, EAVS_SUBMIT_WAIT_MS);
    });
    await Promise.race([
      checkEavsSignal(this.rootStore.apiStore, this.emailField.value),
      budget,
    ]);
    clearTimeout(budgetTimer!);
  }

  private reestablishEavsGate(): void {
    if (!this.isEavsSignalEnabled) {
      return;
    }
    const verdict = peekEavsVerdict(this.emailField.value);
    if (!verdict || !verdict.hasVerdict || verdict.valid) {
      return;
    }
    this.eavsGatedOriginalEmail = this.emailField.value;
    this.eavsGatedReason = verdict.reason;
    this.requireEmailConfirmation = true;
  }

  private static emailDomain(email: string): string {
    return (email.split('@')[1] ?? '').trim().toLowerCase();
  }

  private static normalizeEmail(email: string): string {
    return email.trim().toLowerCase();
  }

  public get eavsImpressionFields(): Record<string, string> {
    return {
      reason: this.eavsGatedReason || 'UNKNOWN',
      emailDomain: SignupStore.emailDomain(this.eavsGatedOriginalEmail),
    };
  }

  public consumeEavsGatedSubmitFields(): void {
    this.eavsGatedSubmitFields = null;
  }

  private buildEavsGatedSubmitFields(): Record<string, string> {
    const original = this.eavsGatedOriginalEmail;
    const submitted = this.emailField.value;
    return {
      reason: this.eavsGatedReason || 'UNKNOWN',
      originalDomain: SignupStore.emailDomain(original),
      finalDomain: SignupStore.emailDomain(submitted),
      emailCorrected: String(
        SignupStore.normalizeEmail(submitted) !==
          SignupStore.normalizeEmail(original),
      ),
      domainCorrected: String(
        SignupStore.emailDomain(submitted) !==
          SignupStore.emailDomain(original),
      ),
    };
  }

  public get isSubmittable(): boolean {
    const shouldRepeatPassword =
      !this.rootStore.displayStore.isMobile && !this.shouldRemoveConfirmFields;
    const emailsMatch = !this.shouldEnforceEmailMatch || this.doEmailsMatch();
    return (
      this.emailField.isValid &&
      emailsMatch &&
      this.passwordField.isValid &&
      (!shouldRepeatPassword ||
        this.passwordField.value === this.passwordRepeatField.value) &&
      !this.isLoading
    );
  }

  private markFormAsDirty() {
    this.emailField.markFieldAsDirty();
    this.emailRepeatField.markFieldAsDirty();
    this.passwordField.markFieldAsDirty();
    this.passwordRepeatField.markFieldAsDirty();
  }

  public reportSwitchToLogin() {
    this.rootStore.biLogger.report(
      loginSignupPageClickOnSwitchForm({
        switch_to: 'login',
        context: 'signup',
      }),
    );
  }

  public onBackClicked() {
    if (this.isEmailSignupMode) {
      this.isEmailSignupMode = false;
      return;
    }
    this.rootStore.navigationStore.goBack();
  }

  public async handleSubmit() {
    const eavsOn = this.isEavsSignalEnabled;
    if (eavsOn && this.isLoading) {
      return;
    }
    if (!this.emailField.isValid) {
      if (eavsOn) {
        this.emailField.markFieldAsDirty();
      }
      return;
    }
    this.isLoading = true;
    this.rootStore.emailStepStore
      .handleEmailSubmit(this.emailField.value, SIGNUP_FLOWS.DEFAULT)
      .catch((e) => {
        if (e.message === ERROR_CODES.ACCOUNT_NOT_FOUND_AND_SIGNUP_DISABLED) {
          this.emailField.addError(
            this.rootStore.i18n.t('account.not.found.error'),
          );
        }
      })
      .finally(() => {
        this.isLoading = false;
      });
  }
}
